/**
 * Validation et normalisation des identifiants d'application (« slug »).
 *
 * Le slug détermine la location Cloudron et donc l'URL publique
 * `https://<slug>.<domaine>`. Référence : cahier des charges §3.2 et §10.
 */
/**
 * Expression imposée par le cahier des charges §10.
 * Se lit : commence par une lettre/chiffre, puis 1 à 38 groupes composés d'un
 * tiret optionnel suivi d'une lettre/chiffre — donc jamais deux tirets
 * consécutifs, jamais de tiret en début ni en fin.
 */
export const SLUG_PATTERN = /^[a-z0-9](-?[a-z0-9]){1,38}$/;
/** Longueurs bornées explicitement : la seule expression ne suffit pas. */
export const SLUG_MIN_LENGTH = 2;
export const SLUG_MAX_LENGTH = 39;
/**
 * Slugs réservés : sous-domaines techniques du Cloudron et de l'infrastructure
 * du portail, qui ne doivent jamais être pris par une application utilisateur.
 */
export const SLUG_BLOCKLIST = [
    'admin',
    'analytics',
    'api',
    'assets',
    'auth',
    'cdn',
    'claude',
    'claude-gateway',
    'cloudron',
    'dashboard',
    'docs',
    'ftp',
    'gateway',
    'grafana',
    'health',
    'imap',
    'login',
    'logout',
    'mail',
    'metrics',
    'my',
    'ns1',
    'ns2',
    'portail',
    'portal',
    'registry',
    'root',
    'smtp',
    'static',
    'status',
    'umami',
    'webmail',
    'www',
];
/**
 * Transforme une saisie libre en slug candidat : minuscules, accents retirés,
 * caractères non alphanumériques remplacés par des tirets, tirets dédoublés et
 * rognés, longueur tronquée. Utilisé en direct dans le formulaire (§3.2).
 */
export function slugify(input) {
    return (input
        .normalize('NFD')
        // Retire les diacritiques (plage Unicode des marques combinantes).
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, SLUG_MAX_LENGTH)
        // La troncature peut laisser un tiret en dernière position.
        .replace(/-$/, ''));
}
/**
 * Valide un slug déjà normalisé et retourne un motif de refus exploitable
 * directement dans l'interface.
 */
export function validateSlug(slug) {
    if (!slug) {
        return { valid: false, code: 'empty', message: "Le nom de l'application est obligatoire." };
    }
    if (slug.length < SLUG_MIN_LENGTH) {
        return {
            valid: false,
            code: 'too_short',
            message: `Le nom doit comporter au moins ${SLUG_MIN_LENGTH} caractères.`,
        };
    }
    if (slug.length > SLUG_MAX_LENGTH) {
        return {
            valid: false,
            code: 'too_long',
            message: `Le nom ne doit pas dépasser ${SLUG_MAX_LENGTH} caractères.`,
        };
    }
    if (!SLUG_PATTERN.test(slug)) {
        return {
            valid: false,
            code: 'invalid_format',
            message: 'Le nom ne peut contenir que des lettres minuscules, des chiffres et des tirets simples, ' +
                'sans tiret au début ni à la fin.',
        };
    }
    if (SLUG_BLOCKLIST.includes(slug)) {
        return {
            valid: false,
            code: 'blocklisted',
            message: `« ${slug} » est un nom réservé par l'infrastructure.`,
        };
    }
    return { valid: true };
}
/** Raccourci booléen de `validateSlug`. */
export function isValidSlug(slug) {
    return validateSlug(slug).valid;
}
//# sourceMappingURL=validate-slug.js.map